import { FormattedMessage } from 'react-intl'

import { AnalyticsAudienceReportingChartsDoughnut } from '@components/Analytics/AudienceReporting/Charts'
import PerformancePlaceholder from '@components/Submission/Performance/Details/Placeholder'
import genderMetrics from '@images/audience-metrics/mock-gender.png'
import countriesMetrics from '@images/audience-metrics/mock-top-countries.png'
import { IAudienceInsights, IBrandConnectAudienceInsight } from '@store/audienceInsights'
import Theme from '@theme'
import { H5 } from '@tribegroup'
import { Container } from '@tribegroup'
import { HorizontalBar } from '@tribegroup/Charts'
import { Chart, ChartsWrapper, PlaceholderImage } from './AudienceInsights.styled'
import InterestSection from './InterestSection'

interface IProps {
  insights: IAudienceInsights | IBrandConnectAudienceInsight
  isTikTok?: boolean
  isYtBrandConnect?: boolean
  isAudienceInterestEnabled?: boolean
}

const ChartsAudienceInsights: React.FC<IProps> = ({
  insights,
  isTikTok,
  isYtBrandConnect,
  isAudienceInterestEnabled,
}) => {
  // Create mutable copies to avoid Redux Toolkit/Immer frozen array errors
  const getGenderData = () => {
    if (isTikTok) {
      return insights.gender?.map((insight) => ({ ...insight, value: insight.percentage }))
    }
    return insights.gender ? [...insights.gender] : undefined
  }

  const getTopCountriesData = () => {
    if (isTikTok) {
      return [...(insights.top_countries || [])].sort((a, b) => b.percentage - a.percentage)
    }
    return insights.top_countries ? [...insights.top_countries] : undefined
  }

  const gender = getGenderData()
  const topCountries = getTopCountriesData()

  const showGrayGenderChart = isTikTok && (!insights.gender || insights.gender.length === 0)
  const showGrayTopCountriesChart =
    isTikTok && (!insights.top_countries || insights.top_countries.length === 0)
  const showTopCities = !isYtBrandConnect

  const isGenderEmpty = !insights.gender || insights.gender.length === 0
  const isAgeEmpty = !insights.age_range || insights.age_range.length === 0
  const isTopCountriesEmpty = !insights.top_countries || insights.top_countries.length === 0
  const isTopCitiesEmpty = !insights.top_cities || insights.top_cities.length === 0

  // Check for BrandConnect-specific interest distributions
  const brandConnectInsights = insights as IBrandConnectAudienceInsight
  const isAffinityInterestsEmpty =
    !brandConnectInsights.affinity_interests || brandConnectInsights.affinity_interests.length === 0
  const isInMarketInterestsEmpty =
    !brandConnectInsights.in_market_interests ||
    brandConnectInsights.in_market_interests.length === 0

  // Include interest data in the empty check for YouTube BrandConnect: affinity/in-market are
  // the only interest charts, and they render on unpublished BrandConnect submissions only.
  const areDistributionChartsEmpty =
    isGenderEmpty && isAgeEmpty && isTopCountriesEmpty && (!showTopCities || isTopCitiesEmpty)
  const hasAffinityInMarketData =
    isYtBrandConnect &&
    isAudienceInterestEnabled &&
    !(isAffinityInterestsEmpty && isInMarketInterestsEmpty)
  const areInterestChartsEmpty = !hasAffinityInMarketData
  const areAllEmpty = areDistributionChartsEmpty && areInterestChartsEmpty

  if (areAllEmpty) {
    return <PerformancePlaceholder type="audience-insights-unavailable" />
  }

  return (
    <ChartsWrapper>
      <Chart data-testid="audience-insights-gender">
        <Container bottomOuterSpacing={1}>
          <H5 color={Theme.grey900Color}>
            <FormattedMessage id="audienceInsights.gender" />
          </H5>
        </Container>
        <AnalyticsAudienceReportingChartsDoughnut
          data={gender ?? []}
          showAsPercentage
          isEmpty={isGenderEmpty}
        />
      </Chart>

      {showGrayGenderChart && (
        <Container relative data-testid="locked-audience-insights-gender">
          <PlaceholderImage src={genderMetrics} />
        </Container>
      )}

      <Chart data-testid="audience-insights-age-range">
        <Container bottomOuterSpacing={1}>
          <H5 color={Theme.grey900Color}>
            <FormattedMessage id="audienceInsights.age_range" />
          </H5>
        </Container>

        <HorizontalBar
          theme={Theme}
          data={insights.age_range ?? []}
          showAsPercentage
          showEmpty={isAgeEmpty}
        />
      </Chart>

      <Chart data-testid="audience-insights-top-countries">
        <Container bottomOuterSpacing={1}>
          <H5 color={Theme.grey900Color}>
            {isTikTok ? (
              <FormattedMessage id="audienceInsights.top_territories" />
            ) : (
              <FormattedMessage id="audienceInsights.top_countries" />
            )}
          </H5>
        </Container>

        <HorizontalBar
          theme={Theme}
          data={topCountries ?? []}
          showAsPercentage
          showEmpty={isTopCountriesEmpty}
        />
      </Chart>

      {showGrayTopCountriesChart && (
        <Container relative data-testid="locked-audience-insights-top-countries">
          <PlaceholderImage src={countriesMetrics} />
        </Container>
      )}

      {showTopCities && (
        <Chart data-testid="audience-insights-top-cities">
          <Container bottomOuterSpacing={1}>
            <H5 color={Theme.grey900Color}>
              <FormattedMessage id="audienceInsights.top_cities" />
            </H5>
          </Container>

          <HorizontalBar
            theme={Theme}
            data={insights.top_cities ?? []}
            showAsPercentage
            showEmpty={isTopCitiesEmpty}
          />
        </Chart>
      )}

      {/* Affinity Interest Section — only valid for BrandConnect views */}
      {isYtBrandConnect && isAudienceInterestEnabled && (
        <InterestSection
          titleI18nKey="audienceInsights.affinity_interest"
          data={brandConnectInsights.affinity_interests ?? []}
        />
      )}

      {/* In-Market Interest Section — only valid for BrandConnect views */}
      {isYtBrandConnect && isAudienceInterestEnabled && (
        <InterestSection
          titleI18nKey="audienceInsights.in_market_interest"
          data={brandConnectInsights.in_market_interests ?? []}
        />
      )}
    </ChartsWrapper>
  )
}

export { ChartsAudienceInsights }
export default ChartsAudienceInsights
